import type {
  IdentityRecord,
  PublicKey,

  VaultData,
  TemporaryContactRecord,
  TemporaryMembershipRecord
} from '@shared/types';
import { openIndexedDb, requestToPromise } from '../utils/idb';
import { createDeviceCryptoKeys, type SigningKey } from './ed25519Provider';
import { base64ToBytes, bytesToBase64, toArrayBuffer } from '../utils/encoding';
import { deriveVaultKey } from './keys';
import {
  canUseNativeTemporaryMembershipStore,
  deleteNativeTemporaryMembership,
  loadNativeTemporaryMembershipsJson,
  saveNativeTemporaryMembership
} from '../utils/webviewBridge';
import type { IdentityAccessKind } from '../utils/identityAccess';

const DB_NAME = 'family-messenger';
const DB_VERSION = 1;
const STORE_NAME = 'keys';

export type WrappedKey = { iv: Uint8Array; data: Uint8Array; v?: 2 };
// v absent = old format (no AAD), v = 2 = new format (with context AAD)

interface StoredKeys {
  // Stored as a non-exportable AES-GCM-256 CryptoKey via structured clone.
  // Used to encrypt/decrypt masterKey bytes and identity private key bytes at rest.
  localWrappingKey?: CryptoKey;

  // MIGRATION: Old format was a JsonWebKey. New format is { iv, data } encrypted with localWrappingKey.
  // Remove old-format handling after June 2026.
  masterKey?: WrappedKey | JsonWebKey;

  identities: Record<string, {
    identityId: string;
    publicKey: PublicKey;
    // Private key bytes encrypted with localWrappingKey (AES-GCM).
    wrappedPrivateKey?: WrappedKey;
    // Optional metadata for UX (safe to add without DB migration)
    serverId?: string;
    serverName?: string;
    serverUrl?: string;
    role?: string;
    accessKind?: IdentityAccessKind;
    accessContextLabel?: string;
    vpsId?: string;
    isServerAdmin?: boolean;
    isPrimaryVpsTarget?: boolean;
    fingerprint?: string;
    identityName?: string;
    publishIdentity?: boolean;
    presenceVisible?: boolean;
    noNamesOnServer?: boolean;
    useForBackupStorage?: boolean;
    pendingRegistration?: Omit<PendingIdentityRegistrationRecord, 'identityId' | 'publicKey' | 'wrappedPrivateKey'>;
    // MIGRATION: Old format stored raw bytes. Remove after June 2026.
    privateKeyBytes?: Uint8Array;
    privateKeyJwk?: JsonWebKey;
  }>;
  devices: Record<string, {
    deviceId: string;
    identityId: string;
    // Ed25519 signing public key. Kept as publicKey for backwards compatibility.
    publicKey: PublicKey;
    // X25519 encryption public key. Missing on legacy devices.
    encryptionPublicKey?: PublicKey | null;
    label?: string;
    signingKey: SigningKey;
    // New format: ecdhScalar wrapped with localWrappingKey (AES-GCM).
    wrappedEcdhScalar?: WrappedKey;
    wrappedPrivateKey?: WrappedKey;
    wrappedEncryptionPrivateKey?: WrappedKey;
    pendingRegistration?: {
      serverUrl: string;
      createdAt: string;
    };
    // MIGRATION: Old format stored ecdhScalar as plaintext bytes. Remove after June 2026.
    ecdhScalar?: Uint8Array;
    // MIGRATION: Even older format stored raw Ed25519 seed bytes. Remove after June 2026.
    privateKeyBytes?: Uint8Array;
    privateKeyJwk?: JsonWebKey;
  }>;
  temporaryMemberships?: Record<string, StoredTemporaryMembershipRecord>;

  // Per-push-subscription context used by Service Worker to identify the source server.
  // Keyed by pushId (server-generated id returned by /api/push/subscribe).
  pushContexts?: Record<string, {
    pushId: string;
    serverOrigin?: string;
    serverId?: string;
    serverName?: string;
    serverUrl?: string;
    updatedAt: string;
  }>;
}

type StoredPushContexts = NonNullable<StoredKeys['pushContexts']>;
export type StoredTemporaryMembershipRecord = TemporaryMembershipRecord & {
  privateKeyBytes: Uint8Array;
  encryptionPrivateKeyBytes?: Uint8Array;
  privateKeyJwk?: JsonWebKey;
  cachedContacts?: TemporaryContactRecord[];
};

export type PushContextRecord = StoredPushContexts[string];

export type PendingIdentityRegistrationRecord = {
  identityId: string;
  publicKey: PublicKey;
  wrappedPrivateKey: WrappedKey;
  serverUrl: string;
  flow: 'circle-registration' | 'group-invite' | 'direct-guest';
  registrationScope: string;
  registrationStatus: 'pending' | 'registered';
  registeredIdentity?: IdentityRecord;
  activationMeta?: {
    serverId?: string;
    serverName?: string;
    serverUrl?: string;
    identityName?: string;
    publishIdentity?: boolean;
    presenceVisible?: boolean;
    accessKind?: IdentityAccessKind;
    accessContextLabel?: string;
    noNamesOnServer?: boolean;
    fingerprint?: string;
    vpsId?: string;
    isServerAdmin?: boolean;
    isPrimaryVpsTarget?: boolean;
  };
  directGuestContext?: {
    linkId: string;
    secret: string;
    contactName: string;
    linkTitle?: string;
    hostKeyFingerprint?: string;
  };
  createdAt: string;
  registeredAt?: string;
};

export type PendingDeviceRegistrationRecord = {
  pendingId: string;
  identityId: string;
  publicKey: PublicKey;
  encryptionPublicKey: PublicKey;
  wrappedPrivateKey: WrappedKey;
  wrappedEncryptionPrivateKey: WrappedKey;
  serverUrl: string;
  label?: string;
  createdAt: string;
};

type StoredLocalVault = {
  vault: VaultData;
  revisionsByIdentityId: Record<string, number>;
  syncPending?: boolean;
  /** @deprecated Legacy field. Kept for migration only. Remove after June 2027. */
  revisionsByServerId?: Record<string, number>;
  updatedAt: string;
};

// Stored separately from the encrypted vault. Used by the Service Worker to localize notifications.
type StoredUiLanguage = string;

type NativeTemporaryMembershipRecord = Omit<StoredTemporaryMembershipRecord, 'privateKeyBytes' | 'encryptionPrivateKeyBytes'> & {
  privateKeyBytes: string;
  encryptionPrivateKeyBytes?: string;
};

/**
 * Open IndexedDB
 */
function openDB(): Promise<IDBDatabase> {
  return openIndexedDb(DB_NAME, DB_VERSION, (db) => {
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      db.createObjectStore(STORE_NAME);
    }
  });
}

/**
 * Get value from store
 */
async function getValue<T>(key: string): Promise<T | undefined> {
  const db = await openDB();
  try {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    return await requestToPromise(store.get(key)) as T | undefined;
  } finally {
    db.close();
  }
}

/**
 * Set value in store
 */
async function setValue<T>(key: string, value: T): Promise<void> {
  const db = await openDB();
  try {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    await requestToPromise(store.put(value, key));
  } finally {
    db.close();
  }
}

/**
 * Delete value from store
 */
async function deleteValue(key: string): Promise<void> {
  const db = await openDB();
  try {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    await requestToPromise(store.delete(key));
  } finally {
    db.close();
  }
}

// In-memory cache so we don't hit IndexedDB on every crypto operation.
let localWrappingKeyCache: CryptoKey | null = null;

/**
 * Load or generate the local wrapping key.
 * Stored as a non-exportable AES-GCM-256 CryptoKey in IndexedDB (structured clone).
 * Its raw bytes are never exposed to JavaScript; protection is against offline disk dumps.
 */
async function getOrCreateLocalWrappingKey(): Promise<CryptoKey> {
  if (localWrappingKeyCache) return localWrappingKeyCache;

  const existing = await getValue<CryptoKey>('localWrappingKey');
  if (existing) {
    localWrappingKeyCache = existing;
    return existing;
  }

  const key = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    false, // non-exportable: raw bytes never appear in JavaScript
    ['encrypt', 'decrypt']
  );
  await setValue('localWrappingKey', key);
  localWrappingKeyCache = key;
  return key;
}

async function wrapBytes(plainBytes: Uint8Array, context: string): Promise<WrappedKey> {
  const key = await getOrCreateLocalWrappingKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const aad = new TextEncoder().encode(context);
  const data = new Uint8Array(await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: aad },
    key,
    toArrayBuffer(plainBytes)
  ));
  return { iv, data, v: 2 };
}

async function unwrapBytes(wrapped: WrappedKey, context: string): Promise<Uint8Array> {
  const key = await getOrCreateLocalWrappingKey();
  if (wrapped.v === 2) {
    const aad = new TextEncoder().encode(context);
    return new Uint8Array(await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: toArrayBuffer(wrapped.iv), additionalData: aad },
      key,
      toArrayBuffer(wrapped.data)
    ));
  }
  // Old format (no AAD) — decrypt and the caller will re-wrap with v:2
  return new Uint8Array(await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: toArrayBuffer(wrapped.iv) },
    key,
    toArrayBuffer(wrapped.data)
  ));
}

/**
 * Create a wrapped identity private key blob for in-memory/runtime use.
 * In WebView mode we still need a wrapped form for existing helper flows,
 * but we do not persist it to IndexedDB.
 */
export async function wrapIdentityPrivateKeyForSession(privateKeyBytes: Uint8Array): Promise<WrappedKey> {
  return wrapBytes(privateKeyBytes, 'circlus:wrap:identity-session');
}

/**
 * Decrypt a wrapped identity private key.
 * Used when raw bytes are needed (e.g. trusted-device transfer payload).
 * Transparently migrates old (no-AAD) wrapped keys to v:2 format in IndexedDB.
 */
export async function unwrapIdentityPrivateKey(wrapped: WrappedKey, identityId: string): Promise<Uint8Array> {
  const context = `circlus:wrap:identity:${identityId}`;
  const bytes = await unwrapBytes(wrapped, context);
  if (!wrapped.v) {
    const migrated = await wrapBytes(bytes, context);
    const identities = await getValue<StoredKeys['identities']>('identities') || {};
    if (identities[identityId]) {
      identities[identityId] = { ...identities[identityId], wrappedPrivateKey: migrated };
      await setValue('identities', identities);
    }
  }
  return bytes;
}

/**
 * Decrypt an in-memory/session wrapped identity private key.
 * WebView keeps identity seeds in the native bridge and wraps them only for the
 * current JS runtime, so those keys use a shared session context instead of the
 * persistent identity-specific context.
 */
export async function unwrapIdentityPrivateKeyForSession(wrapped: WrappedKey): Promise<Uint8Array> {
  return unwrapBytes(wrapped, 'circlus:wrap:identity-session');
}

/**
 * Decrypt a wrapped device ecdhScalar.
 * Transparently migrates old (no-AAD) wrapped keys to v:2 format in IndexedDB.
 */
export async function unwrapDeviceEcdhScalar(wrapped: WrappedKey, deviceId: string): Promise<Uint8Array> {
  const context = `circlus:wrap:device-ecdh:${deviceId}`;
  const bytes = await unwrapBytes(wrapped, context);
  if (!wrapped.v) {
    const migrated = await wrapBytes(bytes, context);
    const devices = await getValue<StoredKeys['devices']>('devices') || {};
    if (devices[deviceId]) {
      devices[deviceId] = { ...devices[deviceId], wrappedEcdhScalar: migrated };
      await setValue('devices', devices);
    }
  }
  return bytes;
}

/**
 * Migrate a device's plaintext ecdhScalar to the wrapped format.
 * Wraps the scalar in place and removes the plaintext field.
 * MIGRATION: Remove after June 2026.
 */
export async function migrateDeviceEcdhScalarToWrapped(deviceId: string, ecdhScalar: Uint8Array): Promise<WrappedKey> {
  const wrappedEcdhScalar = await wrapBytes(ecdhScalar, `circlus:wrap:device-ecdh:${deviceId}`);
  const devices = await getValue<StoredKeys['devices']>('devices') || {};
  if (devices[deviceId]) {
    const entry = { ...devices[deviceId] } as StoredKeys['devices'][string] & { ecdhScalar?: Uint8Array };
    delete entry.ecdhScalar;
    devices[deviceId] = { ...entry, wrappedEcdhScalar };
    await setValue('devices', devices);
  }
  return wrappedEcdhScalar;
}

export async function saveUiLanguage(language: string): Promise<void> {
  const normalized = (language || '').toString().trim().toLowerCase();
  await setValue<StoredUiLanguage>('uiLanguage', normalized);
}

/**
 * Save master key — encrypted at rest with the local wrapping key.
 */
export async function deleteMasterKey(): Promise<void> {
  await deleteValue('masterKey');
}

export async function saveMasterKey(key: CryptoKey): Promise<void> {
  const rawBytes = new Uint8Array(await crypto.subtle.exportKey('raw', key));
  const wrapped = await wrapBytes(rawBytes, 'circlus:wrap:master-key');
  await setValue('masterKey', wrapped);
}

/**
 * Load master key.
 * Handles migration from the old JWK-in-plaintext format and from no-AAD wrapped format.
 */
export async function loadMasterKey(): Promise<CryptoKey | null> {
  const stored = await getValue<WrappedKey | JsonWebKey>('masterKey');
  if (!stored) {
    return null;
  }

  // MIGRATION: Old format was a plain JsonWebKey object.
  // Remove this branch after June 2026 once all clients have migrated.
  if ('k' in (stored as JsonWebKey)) {
    const jwk = stored as JsonWebKey;
    const key = await crypto.subtle.importKey(
      'jwk',
      jwk,
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    );
    // Re-save in new wrapped format (with AAD, v:2).
    await saveMasterKey(key);
    return key;
  }

  // WrappedKey format: decrypt and migrate to v:2 (with AAD) if needed.
  const wrapped = stored as WrappedKey;
  const rawBytes = await unwrapBytes(wrapped, 'circlus:wrap:master-key');
  if (!wrapped.v) {
    // Migrate: re-save with AAD
    await setValue('masterKey', await wrapBytes(rawBytes, 'circlus:wrap:master-key'));
  }
  const key = await crypto.subtle.importKey(
    'raw',
    toArrayBuffer(rawBytes),
    { name: 'AES-GCM', length: 256 },
    true, // must remain extractable for password-based backup and device transfer
    ['encrypt', 'decrypt']
  );
  return key;
}

/**
 * Save identity with private key encrypted at rest using the local wrapping key.
 * Returns the non-exportable CryptoKeys and the wrapped key for immediate use in the store.
 */
export async function saveIdentity(
  identityId: string,
  publicKey: PublicKey,
  privateKeyBytes: Uint8Array,
  meta?: {
    serverId?: string;
    serverName?: string;
    serverUrl?: string;
    role?: string;
    accessKind?: IdentityAccessKind;
    accessContextLabel?: string;
    vpsId?: string;
    isServerAdmin?: boolean;
    isPrimaryVpsTarget?: boolean;
    fingerprint?: string;
    identityName?: string;
    publishIdentity?: boolean;
    presenceVisible?: boolean;
    noNamesOnServer?: boolean;
    useForBackupStorage?: boolean;
  }
): Promise<{ signingKey: SigningKey; ecdhKey: CryptoKey | null; ecdhScalar: Uint8Array; vaultKey: CryptoKey; wrappedPrivateKey: WrappedKey }> {
  const publicKeyBytes = base64ToBytes(publicKey.value);
  const [{ signingKey, ecdhKey, ecdhScalar }, wrappedPrivateKey, vaultKey] = await Promise.all([
    createDeviceCryptoKeys(privateKeyBytes, publicKeyBytes),
    wrapBytes(privateKeyBytes, `circlus:wrap:identity:${identityId}`),
    deriveVaultKey(privateKeyBytes),
  ]);

  const identities = await getValue<StoredKeys['identities']>('identities') || {};
  const nextEntry: StoredKeys['identities'][string] = {
    identityId,
    publicKey,
    wrappedPrivateKey,
    ...meta
  };
  identities[identityId] = nextEntry;
  await setValue('identities', identities);

  return { signingKey, ecdhKey, ecdhScalar, vaultKey, wrappedPrivateKey };
}

/**
 * Load all identities
 */
export async function loadAllIdentities(): Promise<StoredKeys['identities']> {
  return await getValue<StoredKeys['identities']>('identities') || {};
}

/**
 * Save device with non-exportable CryptoKeys derived from raw Ed25519 seed bytes.
 * Returns the created CryptoKeys for immediate use in the store.
 */
export async function saveDevice(
  deviceId: string,
  identityId: string,
  publicKey: PublicKey,
  privateKeyBytes: Uint8Array,
  label?: string,
  encryptionPublicKey?: PublicKey | null,
  encryptionPrivateKeyBytes?: Uint8Array
): Promise<{ signingKey: SigningKey; ecdhKey: CryptoKey | null; ecdhScalar: Uint8Array }> {
  const publicKeyBytes = base64ToBytes(publicKey.value);
  const { signingKey, ecdhKey, ecdhScalar } = await createDeviceCryptoKeys(privateKeyBytes, publicKeyBytes, encryptionPrivateKeyBytes);
  const wrappedEcdhScalar = await wrapBytes(ecdhScalar, `circlus:wrap:device-ecdh:${deviceId}`);

  const devices = await getValue<StoredKeys['devices']>('devices') || {};
  devices[deviceId] = { deviceId, identityId, publicKey, encryptionPublicKey: encryptionPublicKey || null, label, signingKey, wrappedEcdhScalar };
  await setValue('devices', devices);

  return { signingKey, ecdhKey, ecdhScalar };
}

/**
 * Load all devices
 */
export async function loadAllDevices(): Promise<StoredKeys['devices']> {
  return await getValue<StoredKeys['devices']>('devices') || {};
}

/**
 * Save locally generated key material before the corresponding server request.
 * It lives in the regular identity/device collections but remains hidden from
 * active application state until the registration is completed.
 */
export async function savePendingIdentityRegistration(input: {
  identityId: string;
  publicKey: PublicKey;
  privateKeyBytes: Uint8Array;
  serverUrl: string;
  flow?: PendingIdentityRegistrationRecord['flow'];
  registrationScope: string;
}): Promise<void> {
  const identities = await getValue<StoredKeys['identities']>('identities') || {};
  identities[input.identityId] = {
    identityId: input.identityId,
    publicKey: input.publicKey,
    wrappedPrivateKey: await wrapBytes(input.privateKeyBytes, `circlus:wrap:identity:${input.identityId}`),
    serverUrl: input.serverUrl,
    pendingRegistration: {
      serverUrl: input.serverUrl,
      flow: input.flow || 'circle-registration',
      registrationScope: input.registrationScope,
      registrationStatus: 'pending',
      createdAt: new Date().toISOString()
    }
  };
  await setValue('identities', identities);
}

export async function markPendingIdentityRegistrationRegistered(
  identityId: string,
  result?: {
    identity?: IdentityRecord;
    activationMeta?: PendingIdentityRegistrationRecord['activationMeta'];
  }
): Promise<void> {
  const identities = await getValue<StoredKeys['identities']>('identities') || {};
  const identity = identities[identityId];
  const record = identity?.pendingRegistration;
  if (!identity || !record) return;
  identities[identityId] = {
    ...identity,
    pendingRegistration: {
      ...record,
      registrationStatus: 'registered',
      registeredIdentity: result?.identity || record.registeredIdentity,
      activationMeta: result?.activationMeta || record.activationMeta,
      registeredAt: new Date().toISOString()
    }
  };
  await setValue('identities', identities);
}

export async function setPendingIdentityRegistrationDirectGuestContext(
  identityId: string,
  context: NonNullable<PendingIdentityRegistrationRecord['directGuestContext']>
): Promise<void> {
  const identities = await getValue<StoredKeys['identities']>('identities') || {};
  const identity = identities[identityId];
  if (!identity?.pendingRegistration) return;
  identities[identityId] = {
    ...identity,
    pendingRegistration: {
      ...identity.pendingRegistration,
      directGuestContext: context
    }
  };
  await setValue('identities', identities);
}

export async function updatePendingIdentityRegistrationActivationMeta(
  identityId: string,
  activationMeta: PendingIdentityRegistrationRecord['activationMeta']
): Promise<void> {
  const identities = await getValue<StoredKeys['identities']>('identities') || {};
  const identity = identities[identityId];
  if (!identity?.pendingRegistration) return;
  identities[identityId] = {
    ...identity,
    pendingRegistration: {
      ...identity.pendingRegistration,
      activationMeta: {
        ...identity.pendingRegistration.activationMeta,
        ...activationMeta
      }
    }
  };
  await setValue('identities', identities);
}

export async function loadPendingIdentityRegistrations(): Promise<PendingIdentityRegistrationRecord[]> {
  const identities = await getValue<StoredKeys['identities']>('identities') || {};
  return Object.values(identities).flatMap((identity) => identity.pendingRegistration ? [{
    identityId: identity.identityId,
    publicKey: identity.publicKey,
    wrappedPrivateKey: identity.wrappedPrivateKey!,
    ...identity.pendingRegistration
  }] : []);
}

export async function unwrapPendingIdentityRegistrationPrivateKey(record: PendingIdentityRegistrationRecord): Promise<Uint8Array> {
  return unwrapBytes(record.wrappedPrivateKey, `circlus:wrap:identity:${record.identityId}`);
}

export async function deletePendingIdentityRegistration(identityId: string): Promise<void> {
  const identities = await getValue<StoredKeys['identities']>('identities') || {};
  if (identities[identityId]?.pendingRegistration) {
    delete identities[identityId];
    await setValue('identities', identities);
  }
}

export async function savePendingDeviceRegistration(input: {
  identityId: string;
  publicKey: PublicKey;
  privateKeyBytes: Uint8Array;
  encryptionPublicKey: PublicKey;
  encryptionPrivateKeyBytes: Uint8Array;
  serverUrl: string;
  label?: string;
}): Promise<string> {
  const pendingId = `device-${crypto.randomUUID().replace(/-/g, '').slice(0, 12)}`;
  const devices = await getValue<StoredKeys['devices']>('devices') || {};
  const publicKeyBytes = base64ToBytes(input.publicKey.value);
  const [{ signingKey, ecdhScalar }, wrappedPrivateKey, wrappedEncryptionPrivateKey] = await Promise.all([
    createDeviceCryptoKeys(input.privateKeyBytes, publicKeyBytes, input.encryptionPrivateKeyBytes),
    wrapBytes(input.privateKeyBytes, `circlus:wrap:device-signing:${pendingId}`),
    wrapBytes(input.encryptionPrivateKeyBytes, `circlus:wrap:device-encryption:${pendingId}`)
  ]);
  devices[pendingId] = {
    deviceId: pendingId,
    identityId: input.identityId,
    publicKey: input.publicKey,
    encryptionPublicKey: input.encryptionPublicKey,
    label: input.label,
    signingKey,
    wrappedEcdhScalar: await wrapBytes(ecdhScalar, `circlus:wrap:device-ecdh:${pendingId}`),
    wrappedPrivateKey,
    wrappedEncryptionPrivateKey,
    pendingRegistration: {
      serverUrl: input.serverUrl,
      createdAt: new Date().toISOString()
    }
  };
  await setValue('devices', devices);
  return pendingId;
}

export async function deletePendingDeviceRegistration(pendingId: string): Promise<void> {
  const devices = await getValue<StoredKeys['devices']>('devices') || {};
  if (devices[pendingId]?.pendingRegistration) {
    delete devices[pendingId];
    await setValue('devices', devices);
  }
}

export async function loadPendingDeviceRegistrations(identityId: string): Promise<PendingDeviceRegistrationRecord[]> {
  const devices = await getValue<StoredKeys['devices']>('devices') || {};
  return Object.values(devices).flatMap((device) => (
    device.identityId === identityId
    && device.pendingRegistration
    && device.wrappedPrivateKey
    && device.wrappedEncryptionPrivateKey
  ) ? [{
    pendingId: device.deviceId,
    identityId: device.identityId,
    publicKey: device.publicKey,
    encryptionPublicKey: device.encryptionPublicKey!,
    wrappedPrivateKey: device.wrappedPrivateKey,
    wrappedEncryptionPrivateKey: device.wrappedEncryptionPrivateKey,
    serverUrl: device.pendingRegistration.serverUrl,
    label: device.label,
    createdAt: device.pendingRegistration.createdAt
  }] : []);
}

export async function unwrapPendingDeviceRegistrationPrivateKeys(record: PendingDeviceRegistrationRecord): Promise<{
  privateKeyBytes: Uint8Array;
  encryptionPrivateKeyBytes: Uint8Array;
}> {
  const [privateKeyBytes, encryptionPrivateKeyBytes] = await Promise.all([
    unwrapBytes(record.wrappedPrivateKey, `circlus:wrap:device-signing:${record.pendingId}`),
    unwrapBytes(record.wrappedEncryptionPrivateKey, `circlus:wrap:device-encryption:${record.pendingId}`)
  ]);
  return { privateKeyBytes, encryptionPrivateKeyBytes };
}

export async function saveTemporaryMembership(record: StoredTemporaryMembershipRecord): Promise<void> {
  if (canUseNativeTemporaryMembershipStore()) {
    const encoded = encodeNativeTemporaryMembership(record);
    if (saveNativeTemporaryMembership(record.enrollmentId, JSON.stringify(encoded))) {
      await deleteTemporaryMembershipFromIndexedDb(record.enrollmentId);
      return;
    }
  }
  await saveTemporaryMembershipToIndexedDb(record);
}

async function saveTemporaryMembershipToIndexedDb(record: StoredTemporaryMembershipRecord): Promise<void> {
  const temporaryMemberships = await getValue<StoredKeys['temporaryMemberships']>('temporaryMemberships') || {};
  temporaryMemberships[record.enrollmentId] = record;
  await setValue('temporaryMemberships', temporaryMemberships);
}

export async function loadAllTemporaryMemberships(): Promise<Record<string, StoredTemporaryMembershipRecord>> {
  if (canUseNativeTemporaryMembershipStore()) {
    const nativeJson = loadNativeTemporaryMembershipsJson();
    const nativeRows = parseNativeTemporaryMemberships(nativeJson);
    const indexedDbRows = await getValue<Record<string, StoredTemporaryMembershipRecord>>('temporaryMemberships') || {};
    for (const [enrollmentId, record] of Object.entries(indexedDbRows)) {
      if (!nativeRows[enrollmentId] && saveNativeTemporaryMembership(enrollmentId, JSON.stringify(encodeNativeTemporaryMembership(record)))) {
        nativeRows[enrollmentId] = record;
        await deleteTemporaryMembershipFromIndexedDb(enrollmentId);
      }
    }
    return nativeRows;
  }
  return await getValue<Record<string, StoredTemporaryMembershipRecord>>('temporaryMemberships') || {};
}

export async function deleteTemporaryMembership(enrollmentId: string): Promise<void> {
  if (canUseNativeTemporaryMembershipStore()) {
    deleteNativeTemporaryMembership(enrollmentId);
  }
  await deleteTemporaryMembershipFromIndexedDb(enrollmentId);
}

async function deleteTemporaryMembershipFromIndexedDb(enrollmentId: string): Promise<void> {
  const temporaryMemberships = await getValue<StoredKeys['temporaryMemberships']>('temporaryMemberships') || {};
  delete temporaryMemberships[enrollmentId];
  await setValue('temporaryMemberships', temporaryMemberships);
}

function encodeNativeTemporaryMembership(record: StoredTemporaryMembershipRecord): NativeTemporaryMembershipRecord {
  const { privateKeyBytes, encryptionPrivateKeyBytes, ...rest } = record;
  return {
    ...rest,
    privateKeyBytes: bytesToBase64(privateKeyBytes),
    ...(encryptionPrivateKeyBytes ? { encryptionPrivateKeyBytes: bytesToBase64(encryptionPrivateKeyBytes) } : {})
  };
}

function parseNativeTemporaryMemberships(rawJson: string | null): Record<string, StoredTemporaryMembershipRecord> {
  if (!rawJson) return {};
  try {
    const parsed = JSON.parse(rawJson) as Record<string, NativeTemporaryMembershipRecord>;
    const out: Record<string, StoredTemporaryMembershipRecord> = {};
    for (const [id, record] of Object.entries(parsed || {})) {
      if (!record?.privateKeyBytes) continue;
      const { privateKeyBytes, encryptionPrivateKeyBytes, ...rest } = record;
      out[id] = {
        ...rest,
        privateKeyBytes: base64ToBytes(privateKeyBytes),
        ...(encryptionPrivateKeyBytes ? { encryptionPrivateKeyBytes: base64ToBytes(encryptionPrivateKeyBytes) } : {})
      };
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * Update lightweight identity metadata without re-deriving crypto keys.
 * Only patches the specified fields; other stored data is preserved.
 */
export async function updateIdentityMeta(
  identityId: string,
  meta: Partial<{
    serverId: string;
    serverUrl: string;
    role: string;
    accessKind: IdentityAccessKind;
    accessContextLabel: string;
    vpsId: string;
    isServerAdmin: boolean;
    isPrimaryVpsTarget: boolean;
    serverName: string;
    identityName: string;
    publishIdentity: boolean;
    presenceVisible: boolean;
    noNamesOnServer: boolean;
    useForBackupStorage: boolean;
  }>
): Promise<void> {
  const identities = await getValue<StoredKeys['identities']>('identities') || {};
  if (!identities[identityId]) return;
  identities[identityId] = { ...identities[identityId], ...meta };
  await setValue('identities', identities);
}

/**
 * Delete identity by identityId
 */
export async function deleteIdentity(identityId: string): Promise<void> {
  const identities = await getValue<StoredKeys['identities']>('identities') || {};
  delete identities[identityId];
  await setValue('identities', identities);
}

/**
 * Delete device by deviceId
 */
export async function deleteDevice(deviceId: string): Promise<void> {
  const devices = await getValue<StoredKeys['devices']>('devices') || {};
  delete devices[deviceId];
  await setValue('devices', devices);
}

async function loadPushContexts(): Promise<StoredPushContexts> {
  return (await getValue<StoredPushContexts>('pushContexts')) || {};
}

export async function loadPushContextById(pushId: string): Promise<PushContextRecord | null> {
  const normalized = (pushId || '').toString().trim();
  if (!normalized) return null;

  const contexts = await loadPushContexts();
  const record = contexts[normalized];
  return record || null;
}

export async function upsertPushContext(input: {
  pushId: string;
  serverOrigin?: string | null;
  serverId?: string | null;
  serverName?: string | null;
  serverUrl?: string | null;
}): Promise<void> {
  const pushId = (input.pushId || '').toString().trim();
  if (!pushId) return;

  const contexts = await loadPushContexts();
  contexts[pushId] = {
    pushId,
    ...(input.serverOrigin ? { serverOrigin: String(input.serverOrigin) } : {}),
    ...(input.serverId ? { serverId: String(input.serverId) } : {}),
    ...(input.serverName ? { serverName: String(input.serverName) } : {}),
    ...(input.serverUrl ? { serverUrl: String(input.serverUrl) } : {}),
    updatedAt: new Date().toISOString()
  };
  await setValue('pushContexts', contexts);
}

export async function saveLocalVault(
  vault: VaultData,
  revisionsByIdentityId: Record<string, number>,
  syncPending: boolean = false
): Promise<void> {
  const payload: StoredLocalVault = {
    vault,
    revisionsByIdentityId,
    syncPending,
    updatedAt: new Date().toISOString()
  };
  await setValue('localVault', payload);
}

export async function loadLocalVault(): Promise<StoredLocalVault | null> {
  const payload = await getValue<StoredLocalVault>('localVault');
  return payload || null;
}

export async function clearLocalVault(): Promise<void> {
  await deleteValue('localVault');
}
